import { z } from 'zod';
/**
 * Hypothekenvergleicher contracts (W6a) — the public comparison endpoint and
 * the internal CRM "Zinsen" tab.
 *
 * ⚠️ Trade-secret boundary: the PUBLIC response carries provider names only
 * for rates scraped from the banks' own published pages (public_rates). The
 * partner matrix (bank_rates) surfaces publicly ONLY as the anonymous
 * "Finavest Top-Zins" aggregate — never a partner name, id or count.
 */
// ── Vocabulary ───────────────────────────────────────────────────────────────
export const ratesProductSchema = z.enum(['saron', 'fest']);
/** Freshness windows (days/hours) shared by backend logic and CRM badges. */
export const RATES_FRESH_DAYS = 7;
export const RATES_MAX_AGE_DAYS = 10;
export const PUBLIC_RATES_MAX_AGE_HOURS = 48;
// ── GET /public/rates ────────────────────────────────────────────────────────
export const publicRatesQuerySchema = z.object({
    product: ratesProductSchema.optional(),
    /** Fixed-rate term in years; ignored for product=saron. */
    term: z.coerce.number().int().min(1).max(25).optional(),
});
/**
 * Anonymous Finavest top condition per product×term. Entries exist only while
 * the underlying partner data is ≤ RATES_MAX_AGE_DAYS old (otherwise the UI
 * shows "auf Anfrage"); `fresh` distinguishes ≤ RATES_FRESH_DAYS from the
 * 7–10-day twilight zone.
 */
export const publicRatesFinavestEntrySchema = z.object({
    product: ratesProductSchema,
    termYears: z.number().int().nullable(),
    rateFrom: z.number(),
    /**
     * Day precision (UTC midnight, still a datetime string). Deliberately
     * coarser than the provider `stand`s: sheet operations stamp all rows of a
     * partner identically, so ms-precise values would fingerprint which cells
     * share one anonymous partner.
     */
    stand: z.string().datetime(),
    fresh: z.boolean(),
});
/** One named provider row — public Richtsatz from the bank's own page. */
export const publicRatesProviderEntrySchema = z.object({
    label: z.string(),
    product: ratesProductSchema,
    termYears: z.number().int().nullable(),
    rate: z.number(),
    stand: z.string().datetime(),
});
/** Response contract for GET /public/rates — W6b builds against this. */
export const publicRatesSchema = z.object({
    /** max(fetchedAt) over the visible provider rows; null when none. */
    stand: z.string().datetime().nullable(),
    finavest: z.array(publicRatesFinavestEntrySchema),
    providers: z.array(publicRatesProviderEntrySchema),
});
// ── CRM "Zinsen" tab (internal) ──────────────────────────────────────────────
/** Bulk replace of one partner's rate sheet ("Renteblatt erfasst"). */
export const partnerRateSheetSchema = z.object({
    /** Sheet date; becomes validFrom on every row (default: today). */
    validFrom: z.string().datetime().optional(),
    rates: z
        .array(z.object({
        bankProductId: z.string().uuid(),
        /** 0 = SARON (margin); otherwise fixed-rate term in months. */
        termMonths: z.number().int().min(0).max(300),
        ratePct: z.number().gt(0).lt(10),
        ltvBandMaxPct: z.number().gt(0).lte(100).optional(),
    }))
        .min(1)
        .max(200),
});
/** Inline edit of a single partner rate cell. */
export const partnerRateUpdateSchema = z.object({
    ratePct: z.number().gt(0).lt(10),
});
export const rateSourceUpdateSchema = z.object({
    enabled: z.boolean(),
});
